import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Box } from '@material-ui/core';

import {
  BasicTable,
  CustomButton,
  CustomPagination,
  DeleteConfirmationDialog,
  TableEditDeleteActions,
  TableSkeleton,
} from '../../../../../Components';
import { useSelectedTheme, useTranslate } from '../../../../../Hooks';
import {
  DeletePropertyUnitModelAPI,
  GetAllPropertyUnitModelsAPI,
} from '../../../../../Services';
import { UnitsModelsManangementSideMenu } from '../../../NewPropertiesView/PropertyOverview/UI/UnitModels/UI';
import { CCP_UnitModelsTableHeaderData } from './CCP_UnitModelsTableHeaderData';
import { useVerticalNav } from '../../../../../Contexts/VerticalNavContext';
import Divider from '../CPP_PropertyDetailsComponent/Divider';
import UnitTypeSelector from './UnitTypeSelector';

import { PlusIcon } from '../../../../../assets/icons';
import { CompletePropertyProfileContext } from '../../../../../Contexts/CompletePropertyProfileContext/CompletePropertyProfileContext';

import useStyles from './styles';
function CPP_UnitModelComponent({ propertyId, stepId, onStepComplete }) {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { setAlertBoxContent } = useVerticalNav();
  const context = useContext(CompletePropertyProfileContext);
  const { translate } = useTranslate('CompletePropertyProfile');
  const [unitsModels, setUnitsModels] = useState({
    result: [],
    totalCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [activeItem, setActiveItem] = useState({});
  const [pagination, setPagination] = useState({
    pageSize: 25,
    pageIndex: 1,
  });
  const [addUnitModelOpen, setAddUnitModelOpen] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [inputValue, setInputValue] = useState(pagination.pageIndex);

  const getAllUnitsModels = async () => {
    setIsLoading(true);

    const res = await GetAllPropertyUnitModelsAPI({
      propertyId,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    });

    if (!(res && res.status && res.status !== 200)) {
      setUnitsModels({
        result: res.result,
        totalCount: res.totalCount,
      });
    } else {
      setUnitsModels({
        result: [],
        totalCount: 0,
      });
    }

    setIsLoading(false);
  };

  const handleDelete = async () => {
    setIsLoading(true);

    const res = await DeletePropertyUnitModelAPI(
      activeItem?.propertyUnitModelId
    );

    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: 'success',
        title: translate('unit-model-deleted-successfully'),
        onClose: () => setAlertBoxContent(null),
      });
      reloadData();
    } else {
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: 'Failed to delete unit model',
        onClose: () => setAlertBoxContent(null),
      });
    }

    setIsLoading(false);
  };

  const addUnitModelHandler = () => {
    setActiveItem({});
    setAddUnitModelOpen(true);
  };

  const reloadData = () => {
    setOpenDeleteConfirmation(false);
    setActiveItem({});
    getAllUnitsModels();
  };

  const validateForm = useCallback(() => {
    if (unitsModels.totalCount === 0) {
      return { 
        isValid: false, 
        errors: [{ message: translate('please-add-at-least-one-unit-model') }] 
      };
    }
    return { isValid: true, errors: [] };
  }, [unitsModels.totalCount, translate]);

  const onSaveClicked = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: validation.errors[0].message,
        onClose: () => setAlertBoxContent(null),
      });
      return { success: false, errors: validation.errors };
    }
    
    if (onStepComplete) {
      onStepComplete();
    }
    
    setAlertBoxContent({
      display: true,
      variant: 'success',
      title: translate('unit-models-saved-successfully'),
      onClose: () => setAlertBoxContent(null),
    });
    return { success: true };
  }, [validateForm, onStepComplete, setAlertBoxContent, translate]);

  const onNextClicked = useCallback(async () => {
    return await onSaveClicked();
  }, [onSaveClicked]);

  useEffect(() => {
    if (context?.setSaveHandler) context.setSaveHandler(onSaveClicked);
    if (context?.setNextHandler) context.setNextHandler(onNextClicked);
    if (context?.setValidationHandler) context.setValidationHandler(validateForm);

    return () => {
      if (context?.setSaveHandler) context.setSaveHandler(null);
      if (context?.setNextHandler) context.setNextHandler(null);
      if (context?.setValidationHandler) context.setValidationHandler(null);
    };
  }, [onSaveClicked, onNextClicked, validateForm, context]);

  useEffect(() => {
    getAllUnitsModels();
  }, [pagination]);
  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <span className={styles.title}>Unit Model</span>
        <CustomButton
          variant='text'
          boxShadow='none'
          color=''
          hideHoverBg
          onClick={addUnitModelHandler}
          startIcon={
            <PlusIcon
              width='20'
              height='20'
              fill={palette.button.tertiaryGray_fg}
            />
          }
        >
          <span className={styles.textBtn}>Add Unit Model</span>
        </CustomButton>
      </Box>
      <Divider />

      <UnitTypeSelector propertyId={propertyId} />
      <Box className={styles.tableWrapper}>
        {isLoading ? (
          <TableSkeleton rowsNum={12} />
        ) : (
          <BasicTable
            rowsData={
              unitsModels?.result.map((unit) => {
                return {
                  ...unit,
                  id: unit.propertyUnitModelId,
                };
              }) || []
            }
            setActiveItem={setActiveItem}
            rowHeight={84}
            columns={CCP_UnitModelsTableHeaderData(palette, styles)}
            setActiveItemOnRowClick={(item) => {
              setActiveItem(item);
            }}
            tableActions={({ anchorPosition }) => {
              if (!activeItem || Object.keys(activeItem).length === 0)
                return null;
              return (
                <TableEditDeleteActions
                  anchorPosition={anchorPosition}
                  onDelete={() => setOpenDeleteConfirmation(true)}
                  onEdit={() => setAddUnitModelOpen(true)}
                />
              );
            }}
          />
        )}
        <CustomPagination
          totalItems={unitsModels.totalCount}
          itemsPerPage={pagination.pageSize}
          currentPage={pagination.pageIndex}
          inputValue={inputValue}
          setInputValue={setInputValue}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, pageIndex: page }))
          }
          onItemsPerPageChange={(items) =>
            setPagination((prev) => ({ ...prev, pageSize: items }))
          }
          isLoading={isLoading}
          style={{ width: '100%' }}
        />
      </Box>

      {addUnitModelOpen && (
        <UnitsModelsManangementSideMenu
          onOpen={addUnitModelOpen}
          onClose={() => {
            setAddUnitModelOpen(false);
            setActiveItem({});
          }}
          activeItem={activeItem}
          propretyId={propertyId}
          reloadData={() => {
            reloadData();
          }}
        />
      )}
      {openDeleteConfirmation && (
        <DeleteConfirmationDialog
          title='Confirm Deletion'
          subTitle='Are you sure you want to proceed? This action cannot be undone.'
          onClose={() => {
            setOpenDeleteConfirmation(false);
            setActiveItem({});
          }}
          onConfirm={() => {
            handleDelete();
          }}
        />
      )}
    </Box>
  );
}

export default CPP_UnitModelComponent;
